import React from 'react';
import { Link } from 'react-router-dom';
import { Container, Row, Col } from 'reactstrap';
import Navbar from './Navbar';
import Footer from './Footer';
import GuestChatWidget from './GuestChatWidget';
import './landing.scss';
import SeoHead from '../../../Components/Common/SeoHead';
import HelpBreadcrumbSchema from '../../../Components/Common/HelpBreadcrumbSchema';
import HelpScreenshot from './HelpScreenshot';
import type { HelpImage } from './HelpScreenshot';

const HelpArticleStock: React.FC = () => {
  const BASE = '/images/help/stock';

  const screenshots: Record<string, HelpImage[]> = {
    step1: [
      { src: `${BASE}/stk-step-1.png`, alt: 'Stock sync overview', caption: 'Step 1 – How Coderom monitors Amazon stock levels in real time' },
    ],
    step2: [
      { src: `${BASE}/stk-step-2.png`, alt: 'Out-of-stock protection settings', caption: 'Step 2 – Configure out-of-stock protection for your listings' },
    ],
    step3: [
      { src: `${BASE}/stk-step-3.png`, alt: 'Stock quantity settings', caption: 'Step 3 – Set stock quantity rules and safety buffers' },
    ],
    step4: [
      { src: `${BASE}/stk-step-4.png`, alt: 'Re-stock detection interface', caption: 'Step 4 – Automatic re-stock detection and listing reactivation' },
    ],
  };

  return (
    <div className="layout-wrapper landing">
      <SeoHead
        title="Stock Synchronisation & Inventory | CodeRom Help"
        description="Keep eBay stock in sync with Amazon suppliers 24/7 with CodeRom."
      />
      <HelpBreadcrumbSchema slug="stock" name="Stock synchronisation & inventory" />
      <Navbar />

      <div className="help-article-page">
        <Container>
          <Row className="justify-content-center">
            <Col lg={9} xl={8}>
              {/* Breadcrumb */}
              <div className="breadcrumb-section">
                <Link to="/help" className="breadcrumb-link">Help Center</Link>
                <span className="breadcrumb-separator">›</span>
                <span className="breadcrumb-current">Stock synchronisation & inventory</span>
              </div>

              {/* Article Header */}
              <div className="article-header">
                <h1>Stock synchronisation & inventory</h1>
                <p className="article-subtitle">
                  Coderom monitors Amazon stock levels and automatically updates your eBay listings. When a product goes out of stock on Amazon, your eBay listing quantity is set to zero — preventing overselling.
                </p>
                <div className="article-meta">
                  <span className="read-time">📖 6 min read</span>
                  <span className="category">Inventory</span>
                </div>
              </div>

              {/* Table of Contents */}
              <div className="table-of-contents">
                <h3>📋 On this page</h3>
                <ul>
                  <li><a href="#step1">1. How stock sync works</a></li>
                  <li><a href="#step2">2. Out-of-stock protection</a></li>
                  <li><a href="#step3">3. Stock quantity settings</a></li>
                  <li><a href="#step4">4. Re-stock detection</a></li>
                  <li><a href="#step5">5. Stock sync schedule</a></li>
                </ul>
              </div>

              {/* Article Content */}
              <div className="article-content">

                {/* Step 1 */}
                <div id="step1" className="content-section">
                  <h2>Step 1: How stock sync works</h2>
                  <p>
                    Coderom regularly checks whether each product is still available on Amazon. When it detects a change, it updates your eBay listing automatically:
                  </p>
                  <ul>
                    <li><strong>In stock on Amazon</strong> → Your eBay listing shows available quantity</li>
                    <li><strong>Out of stock on Amazon</strong> → Your eBay listing quantity is set to 0 (buyers can't purchase)</li>
                    <li><strong>Back in stock</strong> → Your eBay listing is reactivated automatically</li>
                  </ul>
                  <p>
                    This runs in the background — you don't need to do anything.
                  </p>

                  <HelpScreenshot images={screenshots.step1} stepLabel="Step 1: How stock sync works" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> Stock sync is enabled by default for all products. You don't need to configure anything to get started.
                    </div>
                  </div>
                </div>

                {/* Step 2 */}
                <div id="step2" className="content-section">
                  <h2>Step 2: Out-of-stock protection</h2>
                  <p>
                    This is one of Coderom's most important features. If a buyer purchases a product on eBay but it's out of stock on Amazon, you'd need to cancel the order — damaging your seller rating.
                  </p>
                  <p>
                    Coderom prevents this by:
                  </p>
                  <ol>
                    <li>Checking stock levels every few hours</li>
                    <li>Setting quantity to 0 as soon as a product is unavailable</li>
                    <li>eBay's "Out of Stock" feature keeps the listing active but hidden from search</li>
                  </ol>

                  <HelpScreenshot images={screenshots.step2} stepLabel="Step 2: Out-of-stock protection" />
                </div>

                {/* Step 3 */}
                <div id="step3" className="content-section">
                  <h2>Step 3: Stock quantity settings</h2>
                  <p>
                    Go to <strong>Settings → Stock</strong> to configure:
                  </p>

                  <div className="stock-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Setting</th>
                          <th>What it does</th>
                          <th>Recommended</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td><strong>Default quantity</strong></td>
                          <td>Number of units shown on eBay when in stock</td>
                          <td>1-3</td>
                        </tr>
                        <tr>
                          <td><strong>Out of stock action</strong></td>
                          <td>What happens when Amazon stock runs out</td>
                          <td>Set to 0 (default)</td>
                        </tr>
                        <tr>
                          <td><strong>Use eBay out-of-stock feature</strong></td>
                          <td>Keeps listing active but hidden when quantity is 0</td>
                          <td>On</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <HelpScreenshot images={screenshots.step3} stepLabel="Step 3: Stock quantity settings" />

                  <div className="tip-box">
                    <div className="tip-icon">💡</div>
                    <div className="tip-content">
                      <strong>Tip:</strong> Setting quantity to 1 is safest for dropshipping — it prevents multiple buyers ordering the same item simultaneously.
                    </div>
                  </div>
                </div>

                {/* Step 4 */}
                <div id="step4" className="content-section">
                  <h2>Step 4: Re-stock detection</h2>
                  <p>
                    When a product comes back in stock on Amazon:
                  </p>
                  <ol>
                    <li>Coderom detects the availability change during the next check</li>
                    <li>Your eBay listing quantity is automatically restored</li>
                    <li>The listing becomes visible in eBay search again</li>
                    <li>The price is also re-checked and updated if needed</li>
                  </ol>
                  <p>
                    No manual action required — everything happens automatically.
                  </p>

                  <HelpScreenshot images={screenshots.step4} stepLabel="Step 4: Re-stock detection" />
                </div>

                {/* Step 5 */}
                <div id="step5" className="content-section">
                  <h2>Step 5: Stock sync schedule</h2>
                  <p>
                    Stock is checked on the same smart schedule as pricing:
                  </p>

                  <div className="schedule-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Product status</th>
                          <th>Check frequency</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>Recently sold</td>
                          <td>Every 6 hours</td>
                        </tr>
                        <tr>
                          <td>Active with views</td>
                          <td>Every 12 hours</td>
                        </tr>
                        <tr>
                          <td>Active, low activity</td>
                          <td>Every 24 hours</td>
                        </tr>
                        <tr>
                          <td>Out of stock</td>
                          <td>Every 48 hours</td>
                        </tr>
                        <tr>
                          <td>No sales for 30+ days</td>
                          <td>Every 72 hours</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* What's Next */}
                <div className="whats-next-section">
                  <h2>What's next?</h2>
                  <p>Now that your stock is synced, explore these features:</p>
                  <div className="next-steps">
                    <Link to="/help/amazon-filters" className="next-step-card">
                      <div className="next-step-icon">🔍</div>
                      <div className="next-step-content">
                        <h4>Amazon supplier filters</h4>
                        <p>Set quality rules to automatically select the best Amazon offers</p>
                      </div>
                    </Link>
                    <Link to="/help/pricing" className="next-step-card">
                      <div className="next-step-icon">💰</div>
                      <div className="next-step-content">
                        <h4>Smart repricing & pricing rules</h4>
                        <p>Automatically adjust prices based on Amazon changes and market conditions</p>
                      </div>
                    </Link>
                    <Link to="/help/orders" className="next-step-card">
                      <div className="next-step-icon">📋</div>
                      <div className="next-step-content">
                        <h4>Order management & fulfilment</h4>
                        <p>Automatically process eBay orders and track shipments</p>
                      </div>
                    </Link>
                  </div>
                </div>

                {/* Was This Helpful */}
                <div className="helpful-section">
                  <h3>Was this helpful?</h3>
                  <div className="helpful-buttons">
                    <button className="helpful-btn helpful-yes">👍 Yes</button>
                    <button className="helpful-btn helpful-no">👎 No</button>
                  </div>
                  <p className="helpful-text">
                    Still need help? <Link to="/help">Browse all articles</Link> or{' '}
                    <a href="mailto:support@coderom.com">contact support</a>.
                  </p>
                </div>

              </div>
            </Col>
          </Row>
        </Container>
      </div>

      <style>{`
        .help-article-page {
          padding: 120px 0 80px;
          background: linear-gradient(180deg, #f8fafc 0%, #ffffff 100%);
          min-height: 100vh;
        }

        /* Breadcrumb */
        .breadcrumb-section {
          margin-bottom: 24px;
          font-size: 14px;
        }
        .breadcrumb-link {
          color: #6366f1;
          text-decoration: none;
          transition: color 0.2s;
        }
        .breadcrumb-link:hover {
          color: #4f46e5;
        }
        .breadcrumb-separator {
          margin: 0 8px;
          color: #cbd5e1;
        }
        .breadcrumb-current {
          color: #64748b;
        }

        /* Article Header */
        .article-header {
          margin-bottom: 40px;
        }
        .article-header h1 {
          font-size: 42px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          line-height: 1.2;
        }
        .article-subtitle {
          font-size: 18px;
          color: #64748b;
          line-height: 1.6;
          margin-bottom: 20px;
        }
        .article-meta {
          display: flex;
          gap: 16px;
          align-items: center;
          font-size: 14px;
        }
        .read-time {
          color: #64748b;
        }
        .category {
          background: #f1f5f9;
          color: #6366f1;
          padding: 4px 12px;
          border-radius: 12px;
          font-weight: 500;
        }

        /* Table of Contents */
        .table-of-contents {
          background: #f8fafc;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          padding: 24px;
          margin-bottom: 40px;
        }
        .table-of-contents h3 {
          font-size: 16px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .table-of-contents ul {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .table-of-contents li {
          margin-bottom: 8px;
        }
        .table-of-contents a {
          color: #6366f1;
          text-decoration: none;
          font-size: 15px;
          transition: color 0.2s;
        }
        .table-of-contents a:hover {
          color: #4f46e5;
          text-decoration: underline;
        }

        /* Article Content */
        .article-content {
          color: #334155;
          font-size: 16px;
          line-height: 1.8;
        }
        .content-section {
          margin-bottom: 48px;
          scroll-margin-top: 100px;
        }
        .content-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
          margin-top: 40px;
        }
        .content-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #334155;
          margin-top: 24px;
          margin-bottom: 12px;
        }
        .content-section p {
          margin-bottom: 16px;
        }
        .content-section ul,
        .content-section ol {
          margin-bottom: 16px;
          padding-left: 24px;
        }
        .content-section li {
          margin-bottom: 8px;
        }
        .content-section strong {
          color: #1e293b;
          font-weight: 600;
        }

        /* Tip Box */
        .tip-box {
          display: flex;
          gap: 12px;
          background: #eff6ff;
          border-left: 4px solid #3b82f6;
          padding: 16px;
          border-radius: 8px;
          margin: 24px 0;
        }
        .tip-icon {
          font-size: 20px;
          flex-shrink: 0;
        }
        .tip-content {
          font-size: 15px;
          line-height: 1.6;
          color: #1e40af;
        }

        /* Tables */
        .stock-table,
        .schedule-table {
          margin: 24px 0;
          overflow-x: auto;
        }
        .stock-table table,
        .schedule-table table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }
        .stock-table th,
        .schedule-table th {
          background: #f8fafc;
          color: #1e293b;
          font-weight: 600;
          text-align: left;
          padding: 12px 16px;
          border-bottom: 2px solid #e2e8f0;
          font-size: 14px;
        }
        .stock-table td,
        .schedule-table td {
          padding: 12px 16px;
          border-bottom: 1px solid #f1f5f9;
          font-size: 15px;
        }
        .stock-table tbody tr:last-child td,
        .schedule-table tbody tr:last-child td {
          border-bottom: none;
        }
        .stock-table tbody tr:hover,
        .schedule-table tbody tr:hover {
          background: #f8fafc;
        }

        /* What's Next Section */
        .whats-next-section {
          margin-top: 60px;
          padding-top: 40px;
          border-top: 2px solid #e2e8f0;
        }
        .whats-next-section h2 {
          font-size: 28px;
          font-weight: 700;
          color: #1e293b;
          margin-bottom: 16px;
        }
        .next-steps {
          display: flex;
          flex-direction: column;
          gap: 16px;
          margin-top: 24px;
        }
        .next-step-card {
          display: flex;
          gap: 16px;
          padding: 20px;
          background: white;
          border: 1px solid #e2e8f0;
          border-radius: 12px;
          text-decoration: none;
          transition: all 0.3s;
        }
        .next-step-card:hover {
          border-color: #6366f1;
          box-shadow: 0 4px 12px rgba(99, 102, 241, 0.15);
          transform: translateY(-2px);
        }
        .next-step-icon {
          font-size: 32px;
          flex-shrink: 0;
        }
        .next-step-content h4 {
          font-size: 18px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 4px;
        }
        .next-step-content p {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }

        /* Was This Helpful */
        .helpful-section {
          margin-top: 60px;
          padding: 32px;
          background: #f8fafc;
          border-radius: 12px;
          text-align: center;
        }
        .helpful-section h3 {
          font-size: 20px;
          font-weight: 600;
          color: #1e293b;
          margin-bottom: 20px;
        }
        .helpful-buttons {
          display: flex;
          gap: 12px;
          justify-content: center;
          margin-bottom: 16px;
        }
        .helpful-btn {
          padding: 10px 24px;
          border: 2px solid #e2e8f0;
          background: white;
          border-radius: 8px;
          font-size: 15px;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }
        .helpful-btn:hover {
          border-color: #6366f1;
          background: #eff6ff;
        }
        .helpful-text {
          font-size: 14px;
          color: #64748b;
          margin: 0;
        }
        .helpful-text a {
          color: #6366f1;
          text-decoration: none;
        }
        .helpful-text a:hover {
          text-decoration: underline;
        }

        /* Responsive */
        @media (max-width: 768px) {
          .help-article-page {
            padding: 100px 0 60px;
          }
          .article-header h1 {
            font-size: 32px;
          }
          .article-subtitle {
            font-size: 16px;
          }
          .content-section h2 {
            font-size: 24px;
          }
          .stock-table,
          .schedule-table {
            font-size: 14px;
          }
          .next-step-card {
            flex-direction: column;
            text-align: center;
          }
        }
      `}</style>

      <Footer />
      <GuestChatWidget />
    </div>
  );
};

export default HelpArticleStock;
